import { useEffect, useState, useRef } from 'react'
import { X, ChevronRight, ChevronLeft } from 'lucide-react'

export default function OnboardingTooltip({ 
  target, 
  title, 
  description, 
  step, 
  totalSteps, 
  onNext, 
  onPrev, 
  onSkip,
  position = 'bottom' // 'top', 'bottom', 'left', 'right'
}) {
  const [coords, setCoords] = useState({ top: 0, left: 0 })
  const [arrowPosition, setArrowPosition] = useState('top')
  const tooltipRef = useRef(null)

  useEffect(() => {
    const updatePosition = () => {
      const targetEl = document.querySelector(`[data-tour="${target}"]`)
      if (!targetEl || !tooltipRef.current) return

      const targetRect = targetEl.getBoundingClientRect()
      const tooltipRect = tooltipRef.current.getBoundingClientRect()
      const padding = 12

      let top, left
      let arrow = position

      // Calculate position based on preference
      switch (position) {
        case 'top':
          top = targetRect.top - tooltipRect.height - padding
          left = targetRect.left + (targetRect.width / 2) - (tooltipRect.width / 2)
          arrow = 'bottom'
          break
        case 'bottom':
          top = targetRect.bottom + padding
          left = targetRect.left + (targetRect.width / 2) - (tooltipRect.width / 2)
          arrow = 'top'
          break
        case 'left':
          top = targetRect.top + (targetRect.height / 2) - (tooltipRect.height / 2)
          left = targetRect.left - tooltipRect.width - padding
          arrow = 'right'
          break
        case 'right':
          top = targetRect.top + (targetRect.height / 2) - (tooltipRect.height / 2)
          left = targetRect.right + padding
          arrow = 'left'
          break
        default:
          top = targetRect.bottom + padding
          left = targetRect.left + (targetRect.width / 2) - (tooltipRect.width / 2)
          arrow = 'top'
      }

      // Keep tooltip in viewport
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight

      if (left < 10) left = 10
      if (left + tooltipRect.width > viewportWidth - 10) {
        left = viewportWidth - tooltipRect.width - 10
      }
      if (top < 10) {
        top = targetRect.bottom + padding
        arrow = 'top'
      }
      if (top + tooltipRect.height > viewportHeight - 10) {
        top = targetRect.top - tooltipRect.height - padding
        arrow = 'bottom'
      }

      setCoords({ top, left })
      setArrowPosition(arrow)

      // Highlight target element
      targetEl.style.position = 'relative'
      targetEl.style.zIndex = '1001'
      targetEl.style.boxShadow = '0 0 0 4px rgba(236, 72, 153, 0.5), 0 0 20px rgba(236, 72, 153, 0.3)'
      targetEl.style.borderRadius = '8px'
    }

    updatePosition()
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition)

    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition)
      
      // Remove highlight from target
      const targetEl = document.querySelector(`[data-tour="${target}"]`)
      if (targetEl) {
        targetEl.style.zIndex = ''
        targetEl.style.boxShadow = ''
        targetEl.style.borderRadius = ''
      }
    }
  }, [target, position])

  const arrowStyles = {
    top: 'top-0 left-1/2 -translate-x-1/2 -translate-y-full border-l-transparent border-r-transparent border-t-transparent border-b-ide-surface',
    bottom: 'bottom-0 left-1/2 -translate-x-1/2 translate-y-full border-l-transparent border-r-transparent border-b-transparent border-t-ide-surface',
    left: 'left-0 top-1/2 -translate-x-full -translate-y-1/2 border-t-transparent border-b-transparent border-l-transparent border-r-ide-surface',
    right: 'right-0 top-1/2 translate-x-full -translate-y-1/2 border-t-transparent border-b-transparent border-r-transparent border-l-ide-surface'
  }

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/50 z-[1000]"
        onClick={onSkip}
      />
      
      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className="fixed z-[1002] w-80 bg-ide-surface border border-ide-accent/50 rounded-xl shadow-2xl animate-scale-in"
        style={{ top: coords.top, left: coords.left }}
      >
        {/* Arrow */}
        <div 
          className={`absolute w-0 h-0 border-8 ${arrowStyles[arrowPosition]}`}
        />
        
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-ide-border">
          <div className="flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-ide-accent text-white text-xs font-bold flex items-center justify-center">
              {step}
            </span>
            <span className="text-xs text-ide-textMuted">of {totalSteps}</span>
          </div>
          <button 
            onClick={onSkip}
            className="text-ide-textMuted hover:text-ide-text transition-colors"
            title="Skip tour"
          >
            <X size={16} />
          </button>
        </div>

        {/* Content */}
        <div className="px-4 py-3">
          <h3 className="text-base font-semibold text-ide-text mb-1">{title}</h3>
          <p className="text-sm text-ide-textMuted leading-relaxed">{description}</p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-ide-border bg-ide-bg/50 rounded-b-xl">
          <button
            onClick={onSkip}
            className="text-xs text-ide-textMuted hover:text-ide-text transition-colors"
          >
            Skip tour
          </button>
          <div className="flex items-center gap-2">
            {step > 1 && (
              <button
                onClick={onPrev}
                className="p-1.5 text-ide-textMuted hover:text-ide-text hover:bg-ide-surfaceHover rounded transition-colors"
              >
                <ChevronLeft size={16} />
              </button>
            )}
            <button
              onClick={onNext}
              className="flex items-center gap-1 px-3 py-1.5 bg-ide-accent hover:bg-ide-accentHover text-white text-sm font-medium rounded-lg transition-colors"
            >
              {step === totalSteps ? 'Done' : 'Next'}
              {step < totalSteps && <ChevronRight size={14} />}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
